import { useMemo } from "react";
import { usePersonalAllocations } from "@/queries/allocations/usePersonalAllocations";
import { usePowerGenerationInfo } from "@/queries/user/usePowerGenerationInfo";
import { useUser } from "@/queries/user/useUser";
import { OrderStatus } from "@/api/SolarCloudApi";

export interface LinkCounts {
  gasLinked: number;
  electricityLinked: number;
  bankLinked: number;
  unlinked: number;
  total: number;
  pendingCapacity: number;
}

export function useLinkCounts(): LinkCounts {
  const { data: user } = useUser();
  const { data: allocations } = usePersonalAllocations();
  const { data: powerGenerationInfo } = usePowerGenerationInfo({
    queryConfig: {
      enabled: !!user,
    },
  });

  return useMemo(() => {
    const totalCapacity = powerGenerationInfo?.user?.active ?? 0;
    const totalPanels = totalCapacity / 100;

    // Calculate pending capacity from order summaries
    const pendingCapacity =
      user?.orderSummaries
        ?.filter(
          (order) =>
            order.status !== OrderStatus.DRAFT &&
            order.status !== OrderStatus.FULFILLED &&
            order.status !== OrderStatus.CANCEL_PENDING &&
            order.status !== OrderStatus.CANCELED
        )
        .reduce((sum, order) => {
          return sum + (order.capacityWatts || 0);
        }, 0) || 0;

    if (!allocations) {
      return {
        gasLinked: 0,
        electricityLinked: 0,
        bankLinked: 0,
        unlinked: totalPanels,
        total: totalPanels,
        pendingCapacity,
      };
    }

    const gasLinkedWatts = allocations
      .filter(
        (allocation) =>
          allocation.nickname?.startsWith("::gas::") &&
          allocation.energyCompanyPayee !== null
      )
      .reduce((total, allocation) => total + (allocation.wattCapacity || 0), 0);

    const electricityLinkedWatts = allocations
      .filter(
        (allocation) =>
          !allocation.bankPayee &&
          !allocation.nickname?.startsWith("::gas::") &&
          allocation.energyCompanyPayee !== null
      )
      .reduce((total, allocation) => total + (allocation.wattCapacity || 0), 0);

    const totalLinkedWatts = gasLinkedWatts + electricityLinkedWatts;
    const unlinkedWatts = totalCapacity - totalLinkedWatts;

    const bankLinkedWatts = allocations
      .filter(
        (allocation) => allocation.bankPayee !== null && allocation.bankPayee !== undefined
      )
      .reduce((total, allocation) => total + (allocation.wattCapacity || 0), 0);

    return {
      gasLinked: gasLinkedWatts / 100,
      electricityLinked: electricityLinkedWatts / 100,
      bankLinked: bankLinkedWatts / 100,
      unlinked: unlinkedWatts / 100,
      total: totalPanels,
      pendingCapacity,
    };
  }, [allocations, powerGenerationInfo?.user?.active, user?.orderSummaries]);
}
